import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { UnauthorizedError } from '@/lib/api';
import { type CacheEdit, OPTIMISTIC_KEYS, type Rollback } from '@/lib/optimistic';
import { LIVE_KEYS } from '@/lib/query';

/**
 * Every write the console makes, wrapped once.
 *
 * A hub write is never local: approving a brief queues its tasks, moves the gate
 * counts, changes the stats strip and the project rollups, and writes an audit
 * line the activity rail shows. So rather than each caller guessing which keys it
 * moved, an action invalidates the live set and lets react-query refetch whatever
 * is actually mounted. This is the same instinct the old page's `refreshAll()`
 * had, minus the full-page re-render.
 *
 * A caller that knows what its verb does to a row says so through `optimistic`,
 * and then the row moves on the click rather than on the round trip. The rules
 * that keep that honest are in `lib/optimistic.ts`; the two this hook is
 * responsible for are that the edit is rolled back when the hub refuses, and that
 * the invalidation runs on settle rather than on success, so a refusal is
 * followed by the truth rather than by whatever the cache happened to hold.
 *
 * The error goes to a toast, for the person who clicked and looked away. A
 * caller whose own line is still on screen to print it -- a composer that stays
 * open, a question under a row that is not going anywhere -- says `inlineError`
 * and gets only that: two copies of one sentence, one of them over the row it is
 * about, read as two things going wrong rather than one.
 */
export function useHubAction<TArgs extends unknown[]>(
  action: (...args: TArgs) => Promise<unknown>,
  options: {
    onDone?: () => void;
    /** The line to toast on success. A function where it depends on the verb. */
    success?: string | ((...args: TArgs) => string | undefined);
    /** What this verb does to the lists, applied before the request goes out. */
    optimistic?: (...args: TArgs) => CacheEdit;
    /**
     * The caller renders `error` where the verb was pressed, so the toast would
     * be the second copy.
     *
     * Only for a verb whose question is still on screen when the answer comes
     * back. It is not for a verb with an `optimistic` edit that takes its row
     * away: the box goes with the row, and by the time the rollback puts both
     * back the component that held the error has been through an unmount. That
     * combination reports the failure nowhere at all, which is worse than
     * reporting it twice.
     */
    inlineError?: boolean;
  } = {},
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (args: TArgs) => action(...args),
    onMutate: async (args: TArgs): Promise<Rollback | undefined> => {
      const edit = options.optimistic?.(...args);
      if (!edit) return undefined;
      // A refetch already on the wire would land on top of the edit and put the
      // row back, which looks exactly like the write failing.
      await Promise.all(OPTIMISTIC_KEYS.map((key) => queryClient.cancelQueries({ queryKey: key })));
      return edit(queryClient);
    },
    onSuccess: (_data, args: TArgs) => {
      const line =
        typeof options.success === 'function' ? options.success(...args) : options.success;
      if (line) toast.success(line);
      options.onDone?.();
    },
    onError: (error: unknown, _args, rollback) => {
      rollback?.();
      // A 401 is the session ending, not this write failing. `lib/query.ts` sends
      // it to the gate with one toast of its own, and a second toast reading
      // "unauthorized" would say the same thing in worse words.
      if (error instanceof UnauthorizedError) return;
      if (options.inlineError) return;
      toast.error(error instanceof Error ? error.message : 'that did not work');
    },
    onSettled: () => {
      for (const key of LIVE_KEYS) void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const run = useCallback((...args: TArgs) => mutation.mutate(args), [mutation.mutate]);

  return {
    run,
    busy: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error.message : null,
    reset: mutation.reset,
  };
}
